import { useCallback, useMemo, useState } from "react";
import { Send } from "lucide-react";
import { StyleSheet, View, ScrollView, Pressable } from "react-native";

import { useAppConfig } from "../../../../AppConfigProvider";
import useResponsiveScreen from "../../../../hooks/useResponsiveScreen";
import { AddToMessage } from "../../interfaceComponents/addToMessage/AddToMessage";
import { AttachmentChip } from "../../interfaceComponents/attachmentChip/AttachmentChip";
import CustomButton from "../../../common/general/CustomButton/CustomButton";
import GrowingTextInput from "./GrowingTextInput";
import CustomText from "../../../common/general/CustomText/CustomText";
import { colors, radii, spacing } from "../../../../styles/design-system";

interface SuggestedPrompt {
  text: string;
  color: string;
  textColor?: string;
}

interface AttachedItem {
  id: string;
  name: string;
  duedate?: string | number | Date;
}

interface AttachedFile {
  id: string;
  fileName: string;
  fileURL?: string;
  isLoading?: boolean;
}

interface MessageComposerProps {
  onSendMessage: () => void;
  message: string;
  setMessage: (msg: string) => void;
  addTask?: () => void;
  removeTask?: (taskId: string) => void;
  addAssignment?: () => void;
  removeAssignment?: (assignmentId: string) => void;
  addFile?: () => void;
  removeFile?: (fileId: string) => void;
  attachedTasks?: AttachedItem[];
  attachedAssignments?: AttachedItem[];
  attachedFiles?: AttachedFile[];
  suggestedPrompts?: SuggestedPrompt[];
  placeholder?: string;
  disabled?: boolean;
}

export const MessageComposer: React.FC<MessageComposerProps> = ({
  onSendMessage,
  suggestedPrompts = [],
  placeholder = "Type your message...",
  message,
  setMessage,
  addAssignment,
  removeAssignment,
  addFile,
  removeFile,
  addTask,
  removeTask,
  attachedAssignments = [],
  attachedFiles = [],
  attachedTasks = [],
  disabled = false,
}: MessageComposerProps) => {
  const [isActive, setIsActive] = useState(false);
  const appConfig = useAppConfig();
  const { isLarge } = useResponsiveScreen();
  const primaryColor = appConfig?.primaryColor ?? "#3E68FE";

  const attachedItems = useMemo(
    () => [
      ...attachedAssignments.map((assignment) => ({
        ...assignment,
        type: "assignment" as const,
        isLoading: false,
      })),
      ...attachedTasks.map((task) => ({
        ...task,
        type: "task" as const,
        isLoading: false,
      })),
      ...attachedFiles.map((file) => ({
        id: file.id,
        name: file.fileName,
        type: "file" as const,
        isLoading: file.isLoading ?? false,
        duedate: undefined,
      })),
    ],
    [attachedAssignments, attachedTasks, attachedFiles]
  );

  const removeAttachment = useCallback(
    (id: string, type: "task" | "assignment" | "file") => {
      if (type === "task" && removeTask) {
        removeTask(id);
      } else if (type === "assignment" && removeAssignment) {
        removeAssignment(id);
      } else if (type === "file" && removeFile) {
        removeFile(id);
      }
    },
    [removeTask, removeAssignment, removeFile]
  );

  return (
    <View style={styles.container}>
      {suggestedPrompts.length > 0 && (
        <View style={styles.overflowHidden}>
          <ScrollView
            horizontal
            tabIndex={0}
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={{ width: "100%", justifyContent: "center" }}>
            <View style={[styles.suggestedPromptsWrapper, isLarge && { justifyContent: "center" }]}>
              {suggestedPrompts.map((prompt, index) => (
                <Pressable
                  key={index}
                  style={[styles.promptItem, { backgroundColor: prompt.color }]}
                  onPress={() => {
                    setMessage(prompt.text);
                  }}
                  role="button"
                  accessibilityLabel={prompt.text}
                  accessibilityHint="Select this prompt to use it in your message">
                  <CustomText
                    size="s"
                    useTranslationText={false}
                    text={prompt.text}
                    style={{ color: prompt.textColor ?? colors.black }}
                    numberOfLines={1}
                  />
                </Pressable>
              ))}
            </View>
          </ScrollView>
        </View>
      )}
      <View style={[styles.messageContainer, isActive && styles.activeMessageContainer]}>
        {attachedItems.length > 0 && (
          <View style={styles.overflowHidden}>
            <ScrollView horizontal style={styles.attachedItemsScrollViewWrapper} tabIndex={0}>
              <View style={styles.attachedItemsWrapper}>
                {attachedItems.map((item) => (
                  <AttachmentChip
                    key={item.id}
                    name={item.name}
                    type={item.type}
                    duedate={item.duedate}
                    isLoading={item.isLoading}
                    removeChip={() => {
                      removeAttachment(item.id, item.type);
                    }}
                  />
                ))}
              </View>
            </ScrollView>
          </View>
        )}
        <View style={styles.messageWrapper}>
          <AddToMessage onAddAssignment={addAssignment} onAddTask={addTask} onAddAttachment={addFile} />
          <GrowingTextInput
            value={message}
            setValue={setMessage}
            placeholder={placeholder}
            onKeySubmit={onSendMessage}
            onFocus={() => setIsActive(true)}
            onBlur={() => setIsActive(false)}
          />
          <CustomButton
            leftIcon={<Send size={16} color={colors.white} />}
            disabled={message === "" || disabled}
            style={[styles.sendButton, { backgroundColor: primaryColor }, message === "" && styles.disabledSendButton]}
            onPress={onSendMessage}
            accessibilityLabel="Send message"
          />
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    flexDirection: "column",
    alignItems: "center",
  },
  suggestedPromptsWrapper: {
    display: "flex",
    flexDirection: "row",
    gap: 6,
    paddingBottom: spacing.xs,
    justifyContent: "flex-start",
    width: "100%",
  },
  promptItem: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: radii.full,
  },
  messageContainer: {
    display: "flex",
    alignSelf: "stretch",
    flexDirection: "column",
    borderRadius: spacing.lg,
    borderWidth: 1,
    borderColor: "#e4e7ec",
    backgroundColor: colors.white,
    marginTop: spacing.xs,
    shadowColor: colors.grey[900],
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.06,
    shadowRadius: 10,
  },
  activeMessageContainer: {
    borderColor: colors.brand[200],
    shadowColor: colors.brand[100],
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 3,
  },
  messageWrapper: {
    alignSelf: "stretch",
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    paddingVertical: 6,
    gap: spacing.xs,
  },
  attachedItemsScrollViewWrapper: {
    padding: spacing.xs,
    borderBottomWidth: 1,
    borderBottomColor: "#e0e0e0",
  },
  attachedItemsWrapper: {
    display: "flex",
    flexDirection: "row",
    gap: 6,
    marginBottom: 0,
  },
  sendButton: {
    borderRadius: radii.full,
    padding: 6,
    aspectRatio: 1,
    backgroundColor: "#8a9bff",
  },
  disabledSendButton: {
    opacity: 0.5,
  },
  overflowHidden: {
    alignSelf: "stretch",
    overflow: "hidden",
  },
});
